import { Text } from '@abyss/web/ui/Text';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { Directions } from '../../models/RouteDirections';
import {
  ArrowContainer,
  DetailsContainer,
  DistanceContainer,
  DistanceContainerWidget,
  DurationDistanceWrapper,
  DuratonContainer,
  HorizontalDivider,
  RouteDetailsContainer,
  RouteInstructionsContainer,
  RoutesContainer,
  RoutesTab,
  RoutesTabContainer,
  SeeDirectionsContainer,
  StepContainer,
  StepDataContainer,
  StepIconContainer,
  StepsContainter,
  StepsWrapper,
  TripDurationContainer,
  TripLocationContainer,
  TripLocationDataContainer,
  VerticalDivider,
} from './AmazonMapDisplay.styled';
import { NavigationButtonsRow } from './Navigation/NavigationButtonsRow';

const locationLabelCss = {
  color: '$gray8',
  fontSize: '$smallText',
  fontWeight: '$bold',
  lineHeight: '$sm',
  '@screen < $sm': {
    fontWeight: '$semibold',
  },
};

const locationValueCss = {
  color: '$gray8',
  fontSize: '$md',
  fontWeight: '$medium',
  lineHeight: '$md',
  textWrap: 'nowrap' as const,
  whiteSpace: 'nowrap' as const,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
};

const TripLocationSection = ({
  t,
  mapDirections,
}: {
  t: TFunction;
  mapDirections: Directions;
}) => (
  <React.Fragment>
    <TripLocationContainer>
      <TripLocationDataContainer>
        <Text css={locationLabelCss}>
          A. {t('DISPLAY_DIRECTIONS.START_LOCATION')}
        </Text>
        <Text css={locationValueCss}>{mapDirections.userPlaceName}</Text>
      </TripLocationDataContainer>
      <TripLocationDataContainer>
        <Text css={locationLabelCss}>
          B. {t('DISPLAY_DIRECTIONS.DESTINATION')}
        </Text>
        <Text css={locationValueCss}>{mapDirections.endPlaceName}</Text>
      </TripLocationDataContainer>
    </TripLocationContainer>
    <HorizontalDivider />
  </React.Fragment>
);

const ChevronUp = () => (
  <svg
    fill="none"
    height="18"
    viewBox="0 0 18 18"
    width="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M9 6L4.5 10.5L5.5575 11.5575L9 8.1225L12.4425 11.5575L13.5 10.5L9 6Z"
      fill="$interactive1"
      fillRule="evenodd"
    />
  </svg>
);

const ChevronDown = () => (
  <svg
    fill="none"
    height="18"
    viewBox="0 0 18 18"
    width="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M12.4425 6.44238L9 9.87738L5.5575 6.44238L4.5 7.49988L9 11.9999L13.5 7.49988L12.4425 6.44238Z"
      fill="$interactive1"
      fillRule="evenodd"
    />
  </svg>
);

const SeeDirectionsToggle = ({
  t,
  isWidget,
  showStepInstructions,
  setShowStepInstructions,
}: {
  t: TFunction;
  isWidget: boolean;
  showStepInstructions: boolean;
  setShowStepInstructions: (show: boolean) => void;
}) => (
  <SeeDirectionsContainer
    aria-expanded={showStepInstructions}
    data-auto-testid="see-directions"
    data-testid="see-directions"
    onClick={() => setShowStepInstructions(!showStepInstructions)}
    role="button"
    tabIndex="0"
  >
    <Text
      css={{
        fontWeight: showStepInstructions ? '$bold' : '$normal',
        fontSize: showStepInstructions ? '12.64px' : '$sm',
        lineHeight: showStepInstructions ? '16px' : '18px',
        textWrap: 'nowrap',
        color: '$interactive1',
      }}
    >
      {isWidget
        ? t('DISPLAY_DIRECTIONS.GET_DIRECTIONS')
        : t('DISPLAY_DIRECTIONS.SEE_DIRECTIONS')}
    </Text>
    <ArrowContainer>
      {showStepInstructions ? <ChevronUp /> : <ChevronDown />}
    </ArrowContainer>
  </SeeDirectionsContainer>
);

const RouteSelector = ({
  t,
  mapDirections,
  currentRoute,
  setCurrentRoute,
  loadRoute,
  map,
  longitude,
  latitude,
  routeEndCoords,
}: {
  t: TFunction;
  mapDirections: Directions;
  currentRoute: number;
  setCurrentRoute: (index: number) => void;
  loadRoute: any;
  map: any;
  longitude: string;
  latitude: string;
  routeEndCoords: [number | null, number | null];
}) => (
  <RoutesContainer>
    <Text
      css={{
        color: '$gray8',
        fontSize: '$md',
        fontWeight: '$bold',
        lineHeight: '$md',
      }}
      id="routes"
    >
      {t('DISPLAY_DIRECTIONS.ROUTES')}
    </Text>
    <RoutesTabContainer aria-labelledby="routes" role="group">
      {mapDirections?.routes?.map((_, index: number) => (
        <React.Fragment key={index}>
          <RoutesTab
            aria-pressed={currentRoute === index}
            css={{
              background: index === currentRoute ? '$info1' : 'none',
              color: index === currentRoute ? '$white' : '$gray6',
            }}
            data-auto-testid={`routes-tab-${index}`}
            data-testid={`routes-tab-${index}`}
            key={`routes-tab-${index}`}
            onClick={() => {
              setCurrentRoute(index);
              loadRoute(
                map.current,
                mapDirections?.routes,
                index,
                longitude,
                latitude,
                routeEndCoords[0],
                routeEndCoords[1]
              );
            }}
          >
            {index + 1}
          </RoutesTab>
          {index !== mapDirections.routes.length - 1 && <VerticalDivider />}
        </React.Fragment>
      ))}
    </RoutesTabContainer>
  </RoutesContainer>
);

const stepLabelCss = {
  color: '$gray8',
  display: 'block',
  fontSize: '$smallText',
  lineHeight: '$md',
  fontWeight: '$bold',
};

const stepValueCss = {
  color: '$neutralGray5',
  display: 'block',
  fontSize: '$smallText',
  lineHeight: '$md',
  fontWeight: '$medium',
};

const LocationStep = ({
  label,
  icon,
  placeName,
  testId,
}: {
  label: string;
  icon: string;
  placeName: string;
  testId?: string;
}) => (
  <StepContainer>
    <StepIconContainer>{icon}</StepIconContainer>
    <StepDataContainer>
      <Text css={stepLabelCss}>{label}</Text>
      <Text css={stepValueCss} data-testid={testId}>
        {placeName}
      </Text>
    </StepDataContainer>
  </StepContainer>
);

const StepItem = ({
  distance,
  modifier,
  instruction,
  type,
  index,
  totalSteps,
  mobileScreen,
  getStepIcon,
  getStepDistanceString,
}: {
  distance: number;
  modifier: string | undefined;
  instruction: string;
  type: string;
  index: number;
  totalSteps: number;
  mobileScreen: boolean;
  getStepIcon: any;
  getStepDistanceString: (distance: number) => string;
}) => {
  const isHighlighted =
    (mobileScreen && index === 0) || index === totalSteps - 1;

  return (
    <StepContainer key={`step-container-${index}`}>
      <StepIconContainer>
        <img
          alt=""
          aria-hidden="true"
          src={`data:image/svg+xml,${encodeURIComponent(
            getStepIcon(modifier, type)
          )}`}
        />
      </StepIconContainer>
      <StepDataContainer>
        <Text
          css={{
            color: isHighlighted ? '$gray8' : '$gray7',
            display: 'block',
            fontSize: '$smallText',
            lineHeight: '$md',
            fontWeight: isHighlighted ? '$bold' : '$semibold',
          }}
        >
          {instruction}
        </Text>
        <Text css={stepValueCss}>{getStepDistanceString(distance)}</Text>
      </StepDataContainer>
    </StepContainer>
  );
};

type Props = {
  currentRoute: number;
  getDistanceString(distance: number | undefined): string;
  getDurationString(
    t: TFunction,
    duration: number | undefined,
    isSecondRequired?: boolean
  ): string;
  getNavTypeString(navType: string): string;
  getStepDistanceString(distance: number): string;
  getStepIcon: any;
  handleCloseNav(): void;
  latitude: string;
  loadRoute: any;
  longitude: string;
  map: any;
  mapContainerRef: any;
  mapContainerTestId: string;
  mapDirections: Directions;
  mobileScreen: boolean;
  navType: string;
  routeEndCoords: [number | null, number | null];
  setCurrentRoute(index: number): void;
  setNavType(navType: string): void;
  setShowStepInstructions(show: boolean): void;
  showStepInstructions: boolean;
};

export const MapDirections = ({
  currentRoute,
  getDistanceString,
  getDurationString,
  getNavTypeString,
  getStepDistanceString,
  getStepIcon,
  handleCloseNav,
  latitude,
  loadRoute,
  longitude,
  map,
  mapContainerRef,
  mapContainerTestId,
  mapDirections,
  mobileScreen,
  navType,
  routeEndCoords,
  setCurrentRoute,
  setNavType,
  setShowStepInstructions,
  showStepInstructions,
}: Props) => {
  const { t } = useTranslation();
  const isWidget = useStore(StoreKeys.IS_WIDGET);

  return (
    <React.Fragment>
      <NavigationButtonsRow
        handleCloseNav={handleCloseNav}
        locationForAnalytics={mapContainerTestId}
        navType={navType}
        setNavType={setNavType}
      />
      {!isWidget ? (
        <TripLocationSection mapDirections={mapDirections} t={t} />
      ) : null}
      <TripDurationContainer cssProps={{ isWidget }}>
        <DurationDistanceWrapper
          cssProps={{ isWidget }}
          data-testid={`distance-duration-${navType}`}
        >
          <DuratonContainer>
            {getDurationString(t, mapDirections.routes[currentRoute]?.duration)}{' '}
            {t(getNavTypeString(navType))}
          </DuratonContainer>
          {!isWidget ? (
            <DistanceContainer>
              ({getDistanceString(mapDirections.routes[currentRoute]?.distance)}
              )
            </DistanceContainer>
          ) : (
            <DistanceContainerWidget>
              ({getDistanceString(mapDirections.routes[currentRoute]?.distance)}
              )
            </DistanceContainerWidget>
          )}
        </DurationDistanceWrapper>

        <SeeDirectionsToggle
          isWidget={isWidget}
          setShowStepInstructions={setShowStepInstructions}
          showStepInstructions={showStepInstructions}
          t={t}
        />
      </TripDurationContainer>
      <RouteInstructionsContainer
        css={{ display: showStepInstructions ? 'block' : 'none' }}
        data-testid="route-details-container"
      >
        <HorizontalDivider />

        <RouteDetailsContainer>
          <DetailsContainer>
            <Text
              css={{
                display: 'block',
                fontSize: '$md',
                color: '$gray8',
                fontWeight: '$bold',
                lineHeight: '$md',
              }}
            >
              {getDurationString(
                t,
                mapDirections.routes[currentRoute]?.duration
              )}
            </Text>
            <Text
              css={{
                color: '$gray8',
                marginTop: '$xs',
                display: 'block',
                fontSize: '$md',
                fontWeight: '$medium',
                lineHeight: '$md',
              }}
            >
              {getDistanceString(mapDirections?.routes[currentRoute]?.distance)}
            </Text>
          </DetailsContainer>
        </RouteDetailsContainer>

        <RouteSelector
          currentRoute={currentRoute}
          latitude={latitude}
          loadRoute={loadRoute}
          longitude={longitude}
          map={map}
          mapDirections={mapDirections}
          routeEndCoords={routeEndCoords}
          setCurrentRoute={setCurrentRoute}
          t={t}
        />
        <StepsContainter
          cssProps={{
            mapContainerHeight: mapContainerRef?.current?.clientHeight,
          }}
          tabIndex="0"
        >
          <Text
            css={{
              display: 'block',
              padding: '$md 0',
              fontWeight: '$bold',
              lineHeight: '$md',
              color: '$gray8',
              fontSize: '$md',
            }}
          >
            {t('DISPLAY_DIRECTIONS.STEPS')}
          </Text>
          <StepsWrapper>
            {!mobileScreen && (
              <LocationStep
                icon="A"
                label={t('DISPLAY_DIRECTIONS.START_LOCATION')}
                placeName={mapDirections.userPlaceName}
                testId="start-location"
              />
            )}

            {mapDirections.routes?.[currentRoute]?.steps?.map(
              (
                { distance, maneuver: { modifier, instruction, type } },
                index
              ) => (
                <StepItem
                  distance={distance}
                  getStepDistanceString={getStepDistanceString}
                  getStepIcon={getStepIcon}
                  index={index}
                  instruction={instruction}
                  key={`step-container-${index}`}
                  mobileScreen={mobileScreen}
                  modifier={modifier}
                  totalSteps={
                    mapDirections?.routes?.[currentRoute]?.steps?.length ?? 0
                  }
                  type={type}
                />
              )
            )}

            {!mobileScreen && (
              <LocationStep
                icon="B"
                label={t('DISPLAY_DIRECTIONS.DESTINATION')}
                placeName={mapDirections.endPlaceName}
                testId="destination-location"
              />
            )}
          </StepsWrapper>
        </StepsContainter>
      </RouteInstructionsContainer>
    </React.Fragment>
  );
};
